import { useState } from 'react';
import './FeedIcon.css';

interface FeedIconProps {
  /** Icon URL (favicon / feed image / channel avatar). */
  src?: string;
  /** Twice-the-size variant of `src`, for 2x displays (offered via srcset). */
  src2x?: string;
  alt?: string;
  /** Render as a circle (e.g. YouTube channel avatars) instead of a square. */
  circular?: boolean;
  /** Explicit pixel size; omit to size via `className` CSS. */
  size?: number;
  /** Extra class names for sizing/styling (kept for parity with existing icon CSS). */
  className?: string;
  /** Rendered when there is no `src` or the image fails to load. */
  fallback?: React.ReactNode;
}

/**
 * Feed icon image with square/circle rendering. Circular icons (YouTube channel
 * avatars) get cropped to a circle via the `feedIconCircular` class. Retries once
 * with `no-referrer` before giving up — some hosts 403 hotlinked images with a
 * referrer — and then shows `fallback`.
 */
export default function FeedIcon({ src, src2x, alt = '', circular, size, className, fallback = null }: FeedIconProps) {
  const [referrerPolicy, setReferrerPolicy] = useState<'' | 'no-referrer'>('');
  const [failed, setFailed] = useState(false);

  if (!src || failed) return <>{fallback}</>;

  const cls = [className, circular && 'feedIconCircular'].filter(Boolean).join(' ') || undefined;
  return (
    <img
      src={src}
      srcSet={src2x && `${src} 1x, ${src2x} 2x`}
      alt={alt}
      className={cls}
      {...(size ? { width: size, height: size } : {})}
      referrerPolicy={referrerPolicy || undefined}
      onError={() => referrerPolicy != 'no-referrer' ? setReferrerPolicy('no-referrer') : setFailed(true)}
    />
  );
}
